/**
 * AdminTemplates.tsx
 *
 * Admin-only page for managing carousel templates.
 *
 * Sections:
 *  A) Template Analytics Table — toggle active, expand row for theme breakdown
 *  B) Selector Tester — test which template Gemini would pick for a given thought
 *  C) Template Creation Guide — HTML Template / PPTX Template tabs + standard prompts
 *  D) Validation Checker — paste HTML, validate against Distill requirements
 *
 * Route: /admin/templates
 * Redirects to / if user is not admin.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { usePdfCanvas } from '../../hooks/usePdfCanvas';
import ErrorBoundary from '../../components/ErrorBoundary';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8085';

// Admin allow-list (mirrors Node/src/config/config.ts admin_emails). Keep in sync.
const ADMIN_EMAILS = ['admin@dkafka.com', 'vikas@dkafka.com', 'aditi@dkafka.com'];

// ── Types ─────────────────────────────────────────────────────────────────────

interface ThemeStat {
  key:             string;
  label:           string;
  accentColor:     string;
  approved:        boolean;
  score:           number | null;
  impressionCount: number;
  downloadCount:   number;
}

interface TemplateStat {
  id:                   string;
  name:                 string;
  description:          string;
  bestFor:              string[];
  notFor:               string[];
  slideCount:           number;
  active:               boolean;
  autoDisableCandidate: boolean;
  score:                number | null;
  impressionCount:      number;
  downloadCount:        number;
  updatedAt:            string;
  themes:               ThemeStat[];
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight:  '100vh',
    background: '#0d0d0d',
    color:      '#f0f0f0',
    fontFamily: "'DM Sans', sans-serif",
    padding:    '40px 24px',
    boxSizing:  'border-box' as const,
    overflowX:  'hidden' as const,   // prevent table overflow from creating body scroll
  },
  maxW: {
    maxWidth: '1100px',
    margin:   '0 auto',
    width:    '100%',
  },
  heading: {
    fontSize:    '22px',
    fontWeight:  700,
    color:       '#A89FFF',
    marginBottom: '4px',
  },
  subheading: {
    fontSize:   '13px',
    color:      '#666',
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize:    '15px',
    fontWeight:  600,
    color:       '#A89FFF',
    marginBottom: '12px',
    marginTop:   '40px',
  },
  card: {
    background:   '#161616',
    border:       '1px solid #2a2a2a',
    borderRadius: '10px',
    padding:      '20px',
    marginBottom: '12px',
    overflowX:    'auto' as const,   // table scrolls within card on narrow screens
  },
  table: {
    width:          '100%',
    borderCollapse: 'collapse' as const,
    fontSize:       '13px',
  },
  th: {
    textAlign:     'left' as const,
    padding:       '10px 12px',
    borderBottom:  '1px solid #2a2a2a',
    color:         '#888',
    fontWeight:    500,
  },
  td: {
    padding:       '10px 12px',
    borderBottom:  '1px solid #1a1a1a',
    verticalAlign: 'top' as const,
  },
  btnSmall: {
    padding:      '4px 10px',
    borderRadius: '6px',
    border:       '1px solid #444',
    background:   '#222',
    color:        '#ccc',
    cursor:       'pointer',
    fontSize:     '11px',
  },
  btnPrimary: {
    padding:      '8px 18px',
    borderRadius: '8px',
    border:       'none',
    background:   '#A89FFF',
    color:        '#0d0d0d',
    cursor:       'pointer',
    fontSize:     '13px',
    fontWeight:   600,
  },
  btnSecondary: {
    padding:      '8px 18px',
    borderRadius: '8px',
    border:       '1px solid #444',
    background:   'transparent',
    color:        '#ccc',
    cursor:       'pointer',
    fontSize:     '13px',
  },
  input: {
    width:        '100%',
    padding:      '10px 12px',
    background:   '#111',
    border:       '1px solid #333',
    borderRadius: '8px',
    color:        '#f0f0f0',
    fontSize:     '13px',
    boxSizing:    'border-box' as const,
    resize:       'vertical' as const,
  },
  select: {
    padding:      '9px 12px',
    background:   '#111',
    border:       '1px solid #333',
    borderRadius: '8px',
    color:        '#f0f0f0',
    fontSize:     '13px',
  },
  pre: {
    background:   '#111',
    border:       '1px solid #2a2a2a',
    borderRadius: '8px',
    padding:      '16px',
    fontSize:     '12px',
    overflowX:    'auto' as const,
    color:        '#c8c8c8',
    lineHeight:   '1.6',
    whiteSpace:   'pre-wrap' as const,
    wordBreak:    'break-word' as const,
  },
  expandRow: {
    background:   '#131313',
    fontSize:     '12px',
    color:        '#aaa',
  },
};

// Style functions — kept separate so TypeScript sees them as (arg) => CSSProperties
const sBadge = (active: boolean): React.CSSProperties => ({
  display:      'inline-block',
  padding:      '2px 8px',
  borderRadius: '20px',
  fontSize:     '11px',
  fontWeight:   600,
  background:   active ? '#1a3a1a' : '#2a1a1a',
  color:        active ? '#5fca5f' : '#ca5f5f',
});

const sResultCard = (ok: boolean): React.CSSProperties => ({
  background:   ok ? '#0f1f0f' : '#1f0f0f',
  border:       `1px solid ${ok ? '#2a5a2a' : '#5a2a2a'}`,
  borderRadius: '8px',
  padding:      '14px',
  marginTop:    '12px',
});

const sTab = (active: boolean): React.CSSProperties => ({
  padding:       '7px 16px',
  borderRadius:  '6px',
  border:        'none',
  cursor:        'pointer',
  fontSize:      '13px',
  fontWeight:    active ? 600 : 400,
  background:    active ? '#A89FFF' : 'transparent',
  color:         active ? '#0d0d0d' : '#888',
  marginRight:   '8px',
  transition:    'all 0.15s',
});

// ── Standard prompts (for the Creation Guide section) ─────────────────────────

const HTML_TEMPLATE_PROMPT = `STANDARD PROMPT — HTML TEMPLATE
────────────────────────────────
Create a LinkedIn carousel HTML template for Distill. Requirements:

1. STRUCTURE
   - Single self-contained HTML file with embedded CSS and JS
   - No external dependencies (no CDN links, no external scripts)
   - All fonts must be system fonts or embedded as base64 data URIs

2. DATA CONTRACT
   - Use \`var CAROUSEL_DATA = { ... }\` — MUST use var, NOT const
     (const breaks runtime injection via string replacement)
   - CAROUSEL_DATA must include at minimum:
       colorTheme: string       // passed by the renderer
       authorHandle: string     // injected from user profile
   - Any additional slide data fields must be at the top level

3. RENDER FUNCTION
   - Implement a \`render()\` function (or \`renderSlides(colorTheme)\`)
   - Function reads from CAROUSEL_DATA and renders all slides to the DOM
   - Must be callable after CAROUSEL_DATA is injected

4. SLIDE ELEMENTS
   - Each slide must be a \`<div class="slide">\` (or .slide-col / .slide-scale-box)
   - In preview mode, each slide wrapper must use \`transform: scale(0.28)\`
     so the full-resolution slide fits in the carousel preview thumbnail

5. DIMENSIONS — choose one:
   - Square:   1080 × 1080 px  (most common LinkedIn format)
   - Portrait: 1200 × 1500 px  (tall format — more screen real estate)

6. COLOR THEMES
   - Support colorTheme via CSS custom property --accent
   - Map at minimum: default / warm / cool
   - Apply via: \`document.documentElement.style.setProperty('--accent', color)\`

7. AUTHOR HANDLE
   - Inject authorHandle from CAROUSEL_DATA.authorHandle
   - Show on last slide (brand slide) if included

8. PUPPETEER COMPATIBILITY
   - No external \`<script src="http..."\` tags — they will time out
   - No \`window.location\`, \`navigator.geolocation\`, or hardware APIs
   - No \`localStorage\` or \`sessionStorage\` access

EXAMPLE CAROUSEL_DATA SHAPE (2-slide pink style):
\`\`\`js
var CAROUSEL_DATA = {
  colorTheme: "default",
  authorHandle: "",
  slide1: { headline: "...", badge: "..." },
  slide2: { headline: "...", items: [{ label: "...", tip: "..." }] }
};
\`\`\`

EXAMPLE CAROUSEL_DATA SHAPE (8-slide colorshift style):
\`\`\`js
var CAROUSEL_DATA = {
  template: "colorshift",
  colorTheme: "default",
  authorHandle: "",
  slides: [
    { type: "hook", tag: "...", title: "...", description: "..." },
    { type: "interrupt", tag: "...", title: "...", description: "..." },
    { type: "body", title: "...", description: "..." },
    // ...
    { type: "brand" }
  ]
};
\`\`\``;

const PPTX_TO_HTML_PROMPT = `STANDARD PROMPT — PPTX TO HTML CONVERSION
──────────────────────────────────────────
I have a PowerPoint carousel template. Convert it to an HTML/CSS template for Distill.

The PPTX has [N] slides at [WIDTH × HEIGHT] px dimensions.

Requirements:
1. Recreate every slide's layout using absolute positioning matching PPTX coordinates
   (Convert pt → px: 1pt = 1.333px if needed)

2. Use \`var CAROUSEL_DATA\` (not const) for runtime injection

3. Implement a \`render()\` function that reads from CAROUSEL_DATA and
   populates all text/image slots on every slide

4. Wrap each slide in \`<div class="slide">\` with exact pixel dimensions
   matching the PPTX (e.g., width: 1080px; height: 1080px)

5. In preview mode each .slide must be visible via:
   \`transform: scale(0.28); transform-origin: top left;\`

6. Embed all fonts as base64 woff2 data URIs (no Google Fonts CDN)

7. If the PPTX uses background images, describe them — I will convert to base64

8. Map colorTheme to CSS --accent variable:
   - default: [primary color from PPTX]
   - warm:    [warm variant]
   - cool:    [cool variant]

9. Last slide must always be a brand slide showing authorHandle

10. No external script or stylesheet dependencies

CONTEXT: Distill renders the HTML in Puppeteer headlessly on a Linux server.
The rendered result is exported as a PDF and served to the user.`;

// ── Preview: demo slide data ──────────────────────────────────────────────────
// Passed to POST /api/carousel/render so the backend runs Puppeteer without
// invoking Gemini. Shapes must match the universal CarouselContent contract:
//   { templateId, themeId, slides: [{ slotType, fields }] }
// where slotType + fields keys are defined by the template's manifest under
// src/templates/carousel/manifests/<templateId>.json.

const DEMO_SLIDES: Record<string, any> = {

  // ── sunday-mood — manifest: sunday-mood.json (renderer: pptx) ──────────────
  // Slot sequence: hook (slide 1) → item × 5 (slides 2–6)
  'sunday-mood': {
    templateId: 'sunday-mood',
    themeId:    'sunday-mood-default',
    authorHandle: '@preview',
    slides: [
      {
        slotType: 'hook',
        fields: {
          title_word1: 'My',
          title_word2: 'Morning',
          title_word3: 'Ritual',
          handle:      '@preview',
        },
      },
      {
        slotType: 'item',
        fields: { index: '01', headline: 'Wake before 6am.',          handle: '@preview' },
      },
      {
        slotType: 'item',
        fields: { index: '02', headline: 'No phone for 30 minutes.',   handle: '@preview' },
      },
      {
        slotType: 'item',
        fields: { index: '03', headline: 'Cold water, then coffee.',   handle: '@preview' },
      },
      {
        slotType: 'item',
        fields: { index: '04', headline: 'Write 3 things to finish.',  handle: '@preview' },
      },
      {
        slotType: 'item',
        fields: { index: '05', headline: 'Move your body first.',      handle: '@preview' },
      },
    ],
  },

  // ── marketing-tips — manifest: marketing-tips.json (renderer: pptx) ─────────
  // Slot sequence: hook (slide 1) → body × 7 (slides 2–8)
  'marketing-tips': {
    templateId: 'marketing-tips',
    themeId:    'marketing-tips-default',
    authorHandle: '@preview',
    slides: [
      {
        slotType: 'hook',
        fields: {
          headline: 'Content Strategy That Actually Works',
          handle:   '@preview',
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '[01]',
          headline:    'Know Your Audience',
          explanation: 'Every piece of content should solve a real problem your audience has. Research what questions they are asking and let that drive your content calendar.',
          handle:      '@preview',
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '[02]',
          headline:    'Lead With Value',
          explanation: 'The first line determines if anyone reads the second. Open with a specific insight, surprising fact, or bold claim — not a preamble.',
          handle:      '@preview',
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '[03]',
          headline:    'Consistency Wins',
          explanation: 'One great post per month loses to three good posts per week. Momentum compounds. Build a system you can maintain before optimising for perfection.',
          handle:      '@preview',
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '[04]',
          headline:    'Repurpose Ruthlessly',
          explanation: 'A single idea can live as a carousel, a thread, a newsletter section, and a short video. Create once, distribute many times across the right formats.',
          handle:      '@preview',
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '[05]',
          headline:    'Track What Matters',
          explanation: 'Impressions are vanity. Saves, shares, and replies signal real resonance. Measure what correlates with the outcome you actually want.',
          handle:      '@preview',
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '[06]',
          headline:    'Engage, Don\'t Broadcast',
          explanation: 'Replying to comments within the first hour extends reach significantly. Treat every post as the start of a conversation, not an announcement.',
          handle:      '@preview',
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '[07]',
          headline:    'Iterate From Data',
          explanation: 'Review your top 5 posts each month and find the pattern. Double down on what worked, cut what didn\'t, and test one new variable at a time.',
          handle:      '@preview',
        },
      },
    ],
  },

  // ── white-blue-decision — manifest: white-blue-decision.json (renderer: pptx) ──
  // Slot sequence: hook (slide 1) → body × 9 (slides 2–10)
  'white-blue-decision': {
    templateId:   'white-blue-decision',
    themeId:      'white-blue-decision-default',
    authorHandle: '@preview',
    slides: [
      {
        slotType: 'hook',
        fields: { headline: 'How to Make the Right Decision Every Time' },
      },
      {
        slotType: 'body',
        fields: {
          headline:    'Set clear goals.',
          explanation: 'Before deciding, define what you want to achieve. Clear goals give your decision a measurable benchmark so you can evaluate options objectively.',
        },
      },
      {
        slotType: 'body',
        fields: {
          headline:    'Know the advantages.',
          explanation: 'List every potential benefit of each option. Understanding the upside helps you see which path creates the most value for your situation.',
        },
      },
      {
        slotType: 'body',
        fields: {
          headline:    'Know the disadvantages.',
          explanation: 'Be honest about the downsides. Acknowledging risks early prevents you from being blindsided by consequences you could have foreseen.',
        },
      },
      {
        slotType: 'body',
        fields: {
          headline:    'Consider the possibilities.',
          explanation: 'Expand your thinking beyond the obvious choices. Creative options you have not considered yet may offer the best outcome of all.',
        },
      },
      {
        slotType: 'body',
        fields: {
          headline:    'Think impact.',
          explanation: 'Evaluate how each option affects others — your team, your customers, your long-term goals. A decision that looks good short-term may damage relationships.',
        },
      },
      {
        slotType: 'body',
        fields: {
          headline:    'Keep a diary.',
          explanation: 'Track past decisions and their outcomes. Over time this record reveals your decision patterns and helps you learn from both wins and mistakes.',
        },
      },
      {
        slotType: 'body',
        fields: {
          headline:    'Manage your stress.',
          explanation: 'High stress narrows your thinking. Step away, breathe, and return to the decision when you are calmer — the quality of your choice improves.',
        },
      },
      {
        slotType: 'body',
        fields: {
          headline:    'Give it a time break.',
          explanation: 'Sleep on it. Distance from the decision often brings unexpected clarity and helps you see options you were too close to notice before.',
        },
      },
      {
        slotType: 'body',
        fields: {
          headline:    'Trust your instincts.',
          explanation: 'After thorough analysis, your gut is a valid signal. Experienced pattern recognition often surfaces the right answer faster than any framework.',
        },
      },
    ],
  },

  // ── Social Media Playbook — manifest: pink.json (renderer: pptx) ─────────────
  // Slot sequence: hook (slide 1) → list (slide 2)
  'pink': {
    templateId: 'pink',
    themeId:    'default',
    authorHandle: '@preview',
    slides: [
      {
        slotType: 'hook',
        fields: {
          headline: 'stop the scroll',
          badge:    'content tips',
        },
      },
      {
        slotType: 'list',
        fields: {
          headline: 'post with impact',
          items: [
            'Hook: Lead with a bold claim.',
            'Brevity: One idea per slide.',
            'Visuals: Custom beats stock.',
            'Rhythm: Short sentence, pause.',
            'Engage: Ask one question at end.',
            'CTA: Save this for later.',
          ],
        },
      },
    ],
  },

  // Numbered Tips List — manifest: pink-tips.json
  // Slot sequence: hook → tip × (3–5) → cta
  'pink-tips': {
    templateId: 'pink-tips',
    themeId:    'pink-default',
    authorHandle: '@preview',
    slides: [
      {
        slotType: 'hook',
        fields: {
          number:   '5',
          headline: 'How To Stop The Scroll',
          badge:    'Save This',
        },
      },
      {
        slotType: 'tip',
        fields: {
          index:       '1',
          headline:    'Use a surprising fact up front.',
          explanation: 'Stop the scroll with a number readers cannot resist verifying. Concrete beats clever every time.',
        },
      },
      {
        slotType: 'tip',
        fields: {
          index:       '2',
          headline:    'Custom visuals win.',
          explanation: 'Stock photos signal stock thinking. Hand-drawn or branded visuals create stop-and-stare moments.',
        },
      },
      {
        slotType: 'tip',
        fields: {
          index:       '3',
          headline:    'Less text, more white space.',
          explanation: 'Cramming three thoughts into one slide loses the reader. White space is design, not waste.',
        },
      },
      {
        slotType: 'cta',
        fields: {
          headline: 'Save this for your next post.',
          action:   'Save · Follow · Share',
        },
      },
    ],
  },

  // Decision Making Framework — manifest: colorshift.json (renderer: html)
  // colorshift.html was modernised to read the universal contract
  // ({ slotType, fields }) — same vocabulary as colorshift-framework. The
  // visual design rotates an accent colour across the 4 body slides, so the
  // demo uses the canonical 8-slide structure: hook → interrupt → body×4 →
  // cta → brand.
  'colorshift': {
    templateId: 'colorshift',
    themeId:    'default',
    authorHandle: '@preview',
    slides: [
      {
        slotType: 'hook',
        fields: {
          headline: '4 Habits That Make You Irreplaceable.',
          subhead:  'Most founders work harder. The best ones work differently.',
        },
      },
      {
        slotType: 'interrupt',
        fields: {
          statement: "Hard work alone won't save you. The market doesn't reward effort — it rewards leverage.",
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '1',
          headline:    'Systemise Before You Scale.',
          supportText: "If the business needs you for every decision, you've built a job, not a company. Document the process before you hire for it.",
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '2',
          headline:    'Say No More Than Yes.',
          supportText: "Every yes is a no to something else. Founders who win aren't doing more things — they're doing fewer things exceptionally well.",
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '3',
          headline:    'Make Your Thinking Visible.',
          supportText: 'Write. Share frameworks. Explain how you decide. Thought leadership compounds faster than any ad budget.',
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '4',
          headline:    'Protect Recovery Time.',
          supportText: "Burnout isn't a badge — it's a bug. The founders who last longest treat rest as a competitive advantage.",
        },
      },
      {
        slotType: 'cta',
        fields: {
          headline: 'Which one are you missing?',
          action:   'Save · Comment · Follow',
        },
      },
      {
        slotType: 'brand',
        fields: {
          handle:  '@preview',
          tagline: 'Made with Distill',
        },
      },
    ],
  },

  // Headlines Only — manifest: colorshift-headers.json (renderer: html → colorshift-headers.html)
  // Same Colorshift visual; descriptions stripped — every slide shows ONE headline.
  // Slot sequence: hook → interrupt → body × (1–6) → cta → brand
  'colorshift-headers': {
    templateId: 'colorshift-headers',
    themeId:    'colorshift-default',
    authorHandle: '@preview',
    slides: [
      {
        slotType: 'hook',
        fields: { headline: '4 Habits That Make You Irreplaceable' },
      },
      {
        slotType: 'interrupt',
        fields: { statement: "Hard work alone won't save you. The market rewards leverage, not effort." },
      },
      {
        slotType: 'body',
        fields: { headline: 'Systemise Before You Scale' },
      },
      {
        slotType: 'body',
        fields: { headline: 'Say No More Than Yes' },
      },
      {
        slotType: 'body',
        fields: { headline: 'Make Your Thinking Visible' },
      },
      {
        slotType: 'body',
        fields: { headline: 'Protect Recovery Time' },
      },
      {
        slotType: 'cta',
        fields: { headline: 'Which one are you missing?' },
      },
      {
        slotType: 'brand',
        fields: { handle: '@preview', tagline: 'Made with Distill' },
      },
    ],
  },

  // Framework Breakdown — manifest: colorshift-framework.json
  // Slot sequence: hook → interrupt → body × (1–6) → cta → brand
  'colorshift-framework': {
    templateId: 'colorshift-framework',
    themeId:    'colorshift-default',
    authorHandle: '@preview',
    slides: [
      {
        slotType: 'hook',
        fields: {
          headline: 'A simple framework for clearer thinking',
          subhead:  'Three lenses that turn raw notes into shareable insight.',
        },
      },
      {
        slotType: 'interrupt',
        fields: {
          statement: 'Most carousels fail because the structure was an afterthought, not the thinking.',
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '1',
          headline:    'Start with the contradiction.',
          supportText: 'Find the part of the topic where common advice and reality disagree. That tension is the whole post.',
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '2',
          headline:    'Name the mechanism.',
          supportText: 'Explain *why* the contradiction exists in plain language. No jargon, no hedging.',
        },
      },
      {
        slotType: 'body',
        fields: {
          index:       '3',
          headline:    'Hand them the next move.',
          supportText: 'End every framework slide with one concrete action the reader can take this week.',
        },
      },
      {
        slotType: 'cta',
        fields: {
          headline: 'Try this on your next post.',
          action:   'Save · Follow for more',
        },
      },
      {
        slotType: 'brand',
        fields: {
          handle:  '@preview',
          tagline: 'Made with Distill',
        },
      },
    ],
  },

};

function getDemoSlides(templateId: string, themeId: string): any {
  const base = DEMO_SLIDES[templateId];
  if (!base) {
    // Unknown template — return a stub the backend will reject cleanly so the
    // operator sees a meaningful error rather than an empty render.
    return { templateId, themeId, slides: [] };
  }
  return { ...base, themeId };
}

// ── TemplatePreviewCanvas ─────────────────────────────────────────────────────
// MODE 1 — Single: one slide at a time via usePdfCanvas (same pipeline as Landing.tsx)

interface TemplatePreviewCanvasProps {
  base64Pdf: string | null;
  pageIndex: number;
}

const TemplatePreviewCanvas: React.FC<TemplatePreviewCanvasProps> = ({ base64Pdf, pageIndex }) => {
  const { canvasRef } = usePdfCanvas(base64Pdf, pageIndex);
  if (!base64Pdf) return null;
  return (
    <canvas
      ref={canvasRef}
      style={{ display: 'block', width: '100%', height: 'auto', borderRadius: '4px' }}
    />
  );
};

// ── PdfGrid ───────────────────────────────────────────────────────────────────
// MODE 2 — Grid: all slides rendered simultaneously as live <canvas> elements.
//
// Architecture:
//   PdfGrid   — parses the PDF once, passes the doc object to each GridSlide.
//   GridSlide — renders a single page directly to a <canvas> at containerWidth × DPR.
//               A matchMedia DPR watcher re-renders on Ctrl+/- zoom so slides stay
//               sharp at any zoom level (same behaviour as the single-slide view).
//
// This replaces the old offscreen-canvas → PNG → <img> pipeline which was limited
// to the buffer resolution at render time. Live canvas rendering is infinitely sharp
// because the buffer always matches the current physical pixel count.

const GRID_COL_MIN = 280;

// Renders one PDF page to a canvas, DPR-aware, re-renders on browser zoom.
function usePdfPage(pdfDoc: any, pageIndex: number): React.RefObject<HTMLCanvasElement> {
  const canvasRef     = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<any>(null);
  const dprCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!pdfDoc) return;
    renderPage(pdfDoc, pageIndex);
    return () => {
      renderTaskRef.current?.cancel();
      dprCleanupRef.current?.();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, pageIndex]);

  function renderPage(doc: any, idx: number) {
    dprCleanupRef.current?.();
    dprCleanupRef.current = null;
    (async () => {
      try {
        if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; }
        const page   = await doc.getPage(idx + 1);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const contW  = canvas.parentElement?.clientWidth ?? GRID_COL_MIN;
        // Cap DPR at 2 — grid has 8 simultaneous canvases; DPR=3 mobile would use
        // ~40MB of canvas buffers which risks OOM on low-memory devices.
        const dpr    = Math.min(window.devicePixelRatio || 1, 2);
        const baseVp = page.getViewport({ scale: 1 });
        const scale  = (contW / baseVp.width) * dpr;
        const vp     = page.getViewport({ scale });
        canvas.width  = vp.width;
        canvas.height = vp.height;
        const task = page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp });
        renderTaskRef.current = task;
        await task.promise;
        // Re-render automatically when the user changes browser zoom (DPR changes)
        const mql     = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
        const onZoom  = () => { if (doc) renderPage(doc, idx); };
        mql.addEventListener('change', onZoom, { once: true });
        dprCleanupRef.current = () => mql.removeEventListener('change', onZoom);
      } catch (e: any) {
        if (e?.name !== 'RenderingCancelledException') console.error('[GridSlide] render error', e);
      }
    })();
  }

  return canvasRef;
}

// One slide cell in the grid — renders directly to canvas, no intermediate PNG.
const GridSlide: React.FC<{ pdfDoc: any; pageIndex: number }> = ({ pdfDoc, pageIndex }) => {
  const canvasRef = usePdfPage(pdfDoc, pageIndex);
  return (
    <div style={{ textAlign: 'center' }}>
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: 'auto', borderRadius: '4px', border: '1px solid #222' }}
      />
      <span style={{ fontSize: '10px', color: '#444', marginTop: '4px', display: 'block' }}>
        {pageIndex + 1}
      </span>
    </div>
  );
};

const PdfGrid: React.FC<{ base64Pdf: string | null }> = ({ base64Pdf }) => {
  const [pdfDoc,   setPdfDoc]   = useState<any>(null);
  const [numPages, setNumPages] = useState(0);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (!base64Pdf) { setPdfDoc(null); setNumPages(0); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        (pdfjsLib as any).GlobalWorkerOptions.workerSrc =
          `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as any).version}/pdf.worker.min.js`;
        const bytes = Uint8Array.from(atob(base64Pdf), c => c.charCodeAt(0));
        const doc   = await (pdfjsLib as any).getDocument({ data: bytes }).promise;
        if (!cancelled) { setPdfDoc(doc); setNumPages(doc.numPages); }
      } catch (e) {
        console.error('[PdfGrid] load error', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [base64Pdf]);

  if (!base64Pdf) return null;
  if (loading && !pdfDoc) {
    return <div style={{ padding: '20px 0', fontSize: '12px', color: '#555' }}>Rendering…</div>;
  }

  return (
    <div style={{
      display:             'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_COL_MIN}px, 1fr))`,
      gap:                 '8px',
      padding:             '4px 0 10px',
      width:               '100%',
    }}>
      {Array.from({ length: numPages }, (_, i) => (
        <GridSlide key={i} pdfDoc={pdfDoc} pageIndex={i} />
      ))}
    </div>
  );
};

// ── TemplatePreviewPanel ──────────────────────────────────────────────────────
// Manages fetching preview PDFs (POST /api/carousel/render — Puppeteer only,
// no Gemini) and exposes two view modes:
//
//   • Single — one slide at a time; prev/next navigation (usePdfCanvas)
//   • Strip  — all slides as thumbnails laid out side-by-side (PdfStrip)
//
// Theme switching is instant once allThemePdfs is populated (backend renders
// all approved themes in parallel on the first fetch).

interface TemplatePreviewPanelProps {
  templateId:      string;
  themes:          ThemeStat[];
  active:          boolean;
  /** When true the panel uses /api/admin/carousel/preview (inactive-capable) */
  useAdminPreview?: boolean;
  onActivate?:     () => void;
  onApproveTheme?: (themeKey: string) => void;
}

const TemplatePreviewPanel: React.FC<TemplatePreviewPanelProps> = ({
  templateId,
  themes,
  active,
  useAdminPreview = false,
  onActivate,
  onApproveTheme,
}) => {
  const firstApproved = themes.find(th => th.approved)?.key ?? themes[0]?.key ?? '';
  const [activeTheme, setActiveTheme]       = useState(firstApproved);
  const [allThemePdfs, setAllThemePdfs]     = useState<Record<string, string>>({});
  const [allThemePptxs, setAllThemePptxs]   = useState<Record<string, string>>({});
  const [renderer, setRenderer]             = useState<'html' | 'pptx' | null>(null);
  const [isLoading, setIsLoading]           = useState(false);
  const [altLoading, setAltLoading]         = useState(false);   // background alt-theme render
  const [previewError, setPreviewError]     = useState<string | null>(null);
  const [pageIndex, setPageIndex]           = useState(0);
  const [previewMode, setPreviewMode]       = useState<'single' | 'grid'>('grid');
  const hasFetched                          = useRef(false);

  // Which endpoint to hit: admin-preview bypasses active:true guard
  const renderEndpoint = useAdminPreview
    ? `${API_URL}/api/admin/carousel/preview`
    : `${API_URL}/api/carousel/render`;

  const fetchPreview = useCallback(async (themeKey: string = activeTheme) => {
    setIsLoading(true);
    setPreviewError(null);
    try {
      const token   = localStorage.getItem('distill_auth_token') ?? '';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const demoSlides = getDemoSlides(templateId, themeKey);

      // ── Phase 1: render only the requested theme — fast response ─────────
      const res = await axios.post(
        renderEndpoint,
        { templateId, slides: demoSlides, colorTheme: themeKey, themeIds: [themeKey] },
        { headers, withCredentials: true }
      );

      const data = res.data;
      const detectedRenderer = data.renderer ?? 'html';
      setRenderer(detectedRenderer);

      // Always store PDFs (both HTML and PPTX templates return allThemePdfs for preview)
      if (data.allThemePdfs)  setAllThemePdfs(prev  => ({ ...prev, ...data.allThemePdfs }));
      // Store PPTX binaries when available (PPTX templates only, for download)
      if (data.allThemePptxs) setAllThemePptxs(prev => ({ ...prev, ...data.allThemePptxs }));
      setPageIndex(0);

      // ── Phase 2 (HTML only): background render of remaining approved themes ──
      if (detectedRenderer === 'html') {
        const altKeys = themes.filter(th => th.approved && th.key !== themeKey).map(th => th.key);
        if (altKeys.length > 0) {
          setAltLoading(true);
          axios.post(
            renderEndpoint,
            { templateId, slides: demoSlides, colorTheme: themeKey, themeIds: altKeys },
            { headers, withCredentials: true }
          ).then(altRes => {
            if (altRes.data.allThemePdfs) {
              setAllThemePdfs(prev => ({ ...prev, ...altRes.data.allThemePdfs }));
            }
          }).catch(() => { /* non-critical — admin still sees primary */ })
            .finally(() => setAltLoading(false));
        }
      }
    } catch (err: any) {
      setPreviewError(err?.response?.data?.error ?? err.message ?? 'Render failed');
    } finally {
      setIsLoading(false);
    }
  }, [templateId, activeTheme, themes]);

  // Auto-fetch on first expansion.
  // - Active templates: always fetch.
  // - Inactive templates: only fetch when useAdminPreview=true (admin bypass).
  useEffect(() => {
    if (!active && !useAdminPreview) return;
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchPreview(firstApproved);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, useAdminPreview]);

  const handleThemeSwitch = (themeKey: string) => {
    setActiveTheme(themeKey);
    setPageIndex(0);
    if (!allThemePdfs[themeKey]) fetchPreview(themeKey);
  };

  const activePdf  = allThemePdfs[activeTheme]  ?? null;
  const activePptx = allThemePptxs[activeTheme] ?? null;

  const displayThemes = themes.filter(th => th.approved).length > 0
    ? themes.filter(th => th.approved)
    : themes;

  const modeBtn = (active: boolean): React.CSSProperties => ({
    ...s.btnSmall,
    ...(active
      ? { background: '#A89FFF', color: '#0d0d0d', borderColor: 'transparent', fontWeight: 700 }
      : {}),
  });

  const handleDownloadPdf = () => {
    if (!activePdf) return;
    const bytes = Uint8Array.from(atob(activePdf), c => c.charCodeAt(0));
    const blob  = new Blob([bytes], { type: 'application/pdf' });
    const url   = URL.createObjectURL(blob);
    const a     = document.createElement('a');
    a.href      = url;
    a.download  = `${templateId}-${activeTheme}-preview.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPptx = () => {
    if (!activePptx) return;
    const bytes = Uint8Array.from(atob(activePptx), c => c.charCodeAt(0));
    const blob  = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
    const url   = URL.createObjectURL(blob);
    const a     = document.createElement('a');
    a.href      = url;
    a.download  = `${templateId}-${activeTheme}-preview.pptx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Unified preview (HTML + PPTX templates both render to PDF for visual preview) ──
  return (
    <div style={{ marginTop: '16px' }}>

      {/* Founder action bar — only shown when previewing inactive templates */}
      {!active && (
        <div style={{
          display:      'flex',
          alignItems:   'center',
          gap:          '10px',
          flexWrap:     'wrap',
          padding:      '10px 14px',
          marginBottom: '14px',
          background:   '#0f1f0f',
          border:       '1px solid #2a5a2a',
          borderRadius: '8px',
        }}>
          <span style={{ fontSize: '12px', color: '#5fca5f', fontWeight: 600 }}>
            🔍 Founder Review Mode
          </span>
          <span style={{ fontSize: '12px', color: '#888', flex: 1 }}>
            Preview renders using demo slides. Activate when satisfied.
          </span>
          {onActivate && (
            <button
              style={{
                padding:      '5px 14px',
                borderRadius: '6px',
                border:       'none',
                background:   '#5fca5f',
                color:        '#0d0d0d',
                cursor:       'pointer',
                fontSize:     '12px',
                fontWeight:   700,
              }}
              onClick={onActivate}
            >
              ✓ Activate Template
            </button>
          )}
        </div>
      )}

      {/* Header row: label + mode toggle + download buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '11px', color: '#555', fontWeight: 600, letterSpacing: '0.05em' }}>
          PREVIEW
        </div>
        {renderer === 'pptx' && (
          <span style={{
            fontSize: '11px', color: '#A89FFF',
            background: '#1a1a2e', borderRadius: '4px', padding: '2px 7px',
          }}>
            PPTX
          </span>
        )}
        <div style={{ display: 'flex', gap: '4px' }}>
          <button style={modeBtn(previewMode === 'single')} onClick={() => setPreviewMode('single')}>
            Single
          </button>
          <button style={modeBtn(previewMode === 'grid')} onClick={() => setPreviewMode('grid')}>
            Grid
          </button>
        </div>
        <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
          {activePdf && (
            <button
              style={s.btnSmall}
              onClick={handleDownloadPdf}
              title={`Download ${templateId}-${activeTheme} preview as PDF`}
            >
              ⬇ PDF
            </button>
          )}
          {activePptx && (
            <button
              style={s.btnSmall}
              onClick={handleDownloadPptx}
              title={`Download ${templateId}-${activeTheme} as PPTX`}
            >
              ⬇ PPTX
            </button>
          )}
        </div>
      </div>

      {/* Theme selector + refresh */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        {displayThemes.length > 1 && displayThemes.map(th => {
          const isReady   = !!allThemePdfs[th.key];
          const isPending = altLoading && !isReady && th.key !== activeTheme;
          return (
            <button
              key={th.key}
              style={{
                ...s.btnSmall,
                ...(activeTheme === th.key
                  ? { background: th.accentColor || '#A89FFF', color: '#0d0d0d', borderColor: 'transparent' }
                  : {}),
                ...(isPending ? { opacity: 0.45, cursor: 'default' } : {}),
              }}
              onClick={() => !isPending && handleThemeSwitch(th.key)}
              title={isPending ? `${th.label} — loading…` : th.label}
              disabled={isPending}
            >
              {th.label}{isPending ? ' ·' : ''}
            </button>
          );
        })}
        <button
          style={s.btnSmall}
          onClick={() => fetchPreview(activeTheme)}
          disabled={isLoading}
          title="Re-render preview"
        >
          ↺{isLoading ? ' …' : ''}
        </button>
        {altLoading && (
          <span style={{ fontSize: '11px', color: '#555' }}>loading themes…</span>
        )}
      </div>

      {isLoading && !activePdf && (
        <div style={{ padding: '32px 0', fontSize: '12px', color: '#555' }}>Rendering…</div>
      )}
      {previewError && !activePdf && (
        <div style={{ padding: '14px', fontSize: '12px', color: '#ca5f5f' }}>
          Preview failed: {previewError}
        </div>
      )}

      {previewMode === 'single' && (
        <>
          <div style={{
            position: 'relative', background: '#111',
            borderRadius: '6px', overflow: 'hidden', maxWidth: '280px',
          }}>
            {isLoading && activePdf && (
              <div style={{
                position: 'absolute', inset: 0, zIndex: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.55)', fontSize: '12px', color: '#888',
              }}>
                Rendering…
              </div>
            )}
            <TemplatePreviewCanvas base64Pdf={activePdf} pageIndex={pageIndex} />
          </div>
          {activePdf && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
              <button style={s.btnSmall} disabled={pageIndex === 0}
                onClick={() => setPageIndex(p => Math.max(0, p - 1))}>
                ‹ Prev
              </button>
              <span style={{ fontSize: '12px', color: '#555' }}>Slide {pageIndex + 1}</span>
              <button style={s.btnSmall} onClick={() => setPageIndex(p => p + 1)}>
                Next ›
              </button>
            </div>
          )}
        </>
      )}

      {previewMode === 'grid' && <PdfGrid base64Pdf={activePdf} />}

    </div>
  );
};

// ── AdminTemplates component ──────────────────────────────────────────────────

const AdminTemplates: React.FC = () => {
  // ── Auth guard ──────────────────────────────────────────────────────────────
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);

  useEffect(() => {
    // Use same token key as Landing.tsx
    const token = localStorage.getItem('distill_auth_token');

    if (!token) {
      setAuthChecked(true);
      return;
    }

    // Decode JWT to check role/email without a round-trip
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp > Date.now() / 1000) {
        if (payload.role === 'admin' || ADMIN_EMAILS.includes((payload.email || '').toLowerCase())) {
          setIsAdminUser(true);
        }
      }
    } catch {
      // malformed JWT — fall back to API check
    }

    axios
      .get(`${API_URL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      })
      .then(res => {
        if (res.data?.role === 'admin' || ADMIN_EMAILS.includes((res.data?.email || '').toLowerCase())) {
          setIsAdminUser(true);
        }
      })
      .catch(() => {})
      .finally(() => setAuthChecked(true));
  }, []);

  // ── Section A: Template list ────────────────────────────────────────────────
  const [templates, setTemplates]         = useState<TemplateStat[]>([]);
  const [loadingList, setLoadingList]     = useState(false);
  const [expandedId, setExpandedId]       = useState<string | null>(null);
  const [togglingId, setTogglingId]         = useState<string | null>(null);
  const [deletingId, setDeletingId]         = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [approvingTheme, setApprovingTheme] = useState<string | null>(null); // "templateId:themeKey"

  const loadTemplates = useCallback(async () => {
    const token = localStorage.getItem('distill_auth_token');
    if (!token) return;
    setLoadingList(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(res.data);
    } catch {
      // swallow — will show empty state
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (isAdminUser) loadTemplates();
  }, [isAdminUser, loadTemplates]);

  const handleToggle = async (id: string) => {
    const token = localStorage.getItem('distill_auth_token');
    if (!token || togglingId) return;
    setTogglingId(id);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/templates/${id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(prev =>
        prev.map(t => (t.id === id ? { ...t, active: res.data.active } : t))
      );
    } catch {
      // swallow
    } finally {
      setTogglingId(null);
    }
  };

  const handleApproveTheme = async (templateId: string, themeKey: string) => {
    const token = localStorage.getItem('distill_auth_token');
    if (!token) return;
    const key = `${templateId}:${themeKey}`;
    if (approvingTheme === key) return;
    setApprovingTheme(key);
    try {
      await axios.post(
        `${API_URL}/api/admin/carousel/themes/${templateId}/${themeKey}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      // Update local state so badge flips to "Yes" immediately
      setTemplates(prev =>
        prev.map(t => t.id === templateId
          ? {
            ...t,
            themes: t.themes.map(th =>
              th.key === themeKey ? { ...th, approved: true } : th
            ),
          }
          : t
        )
      );
    } catch {
      // swallow
    } finally {
      setApprovingTheme(null);
    }
  };

  const handleDelete = async (id: string) => {
    const token = localStorage.getItem('distill_auth_token');
    if (!token || deletingId) return;
    setDeletingId(id);
    setConfirmDeleteId(null);
    try {
      await axios.delete(
        `${API_URL}/api/admin/templates/${id}`,
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      setTemplates(prev => prev.filter(t => t.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Delete failed';
      alert(msg);  // surface the 409 "disable first" message clearly
    } finally {
      setDeletingId(null);
    }
  };

  // ── Section B: Selector tester ──────────────────────────────────────────────
  const [selectorThought, setSelectorThought] = useState('');
  const [selectorTopic, setSelectorTopic]     = useState('');
  const [selectorAngle, setSelectorAngle]     = useState('thoughtful');
  const [selectorResult, setSelectorResult]   = useState<any>(null);
  const [selectorLoading, setSelectorLoading] = useState(false);
  const [selectorError, setSelectorError]     = useState('');
  const [traceOpen, setTraceOpen]             = useState(true);

  const handleTestSelector = async () => {
    if (!selectorThought.trim()) return;
    const token = localStorage.getItem('distill_auth_token');
    if (!token) return;
    setSelectorLoading(true);
    setSelectorError('');
    setSelectorResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/templates/test-selector`,
        { thought: selectorThought, angle: selectorAngle, topic: selectorTopic || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectorResult(res.data);
    } catch (err: any) {
      setSelectorError(err?.response?.data?.error || 'Selector test failed');
    } finally {
      setSelectorLoading(false);
    }
  };

  // ── Section C: Creation guide ───────────────────────────────────────────────
  const [guideTab, setGuideTab] = useState<'html' | 'pptx'>('html');
  const [copiedPrompt, setCopiedPrompt] = useState(false);

  const handleCopyPrompt = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 2000);
    });
  };

  // ── Section D: Validation checker ──────────────────────────────────────────
  const [validateHtml, setValidateHtml]         = useState('');
  const [validateId, setValidateId]             = useState('');
  const [validateType, setValidateType]         = useState<'html' | 'pptx'>('html');
  const [validatePptxFile, setValidatePptxFile] = useState<File | null>(null);
  const [validateResult, setValidateResult]     = useState<{
    valid: boolean;
    errors: string[];
    pptxSaved?: boolean;
  } | null>(null);
  const [validateLoading, setValidateLoading]   = useState(false);

  const handleValidate = async () => {
    if (!validateHtml.trim()) return;
    const token = localStorage.getItem('distill_auth_token');
    if (!token) return;
    setValidateLoading(true);
    setValidateResult(null);
    try {
      let res;
      if (validatePptxFile) {
        // Use FormData so the PPTX binary can be sent alongside text fields
        const form = new FormData();
        form.append('html', validateHtml);
        form.append('templateId', validateId);
        form.append('templateType', validateType);
        form.append('pptx', validatePptxFile);
        res = await axios.post(
          `${API_URL}/api/admin/templates/validate`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        res = await axios.post(
          `${API_URL}/api/admin/templates/validate`,
          { html: validateHtml, templateId: validateId, templateType: validateType },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setValidateResult(res.data);
    } catch (err: any) {
      setValidateResult({
        valid:  false,
        errors: [err?.response?.data?.error || 'Validation request failed'],
      });
    } finally {
      setValidateLoading(false);
    }
  };

  // ── Guard: wait for auth check ──────────────────────────────────────────────
  if (!authChecked) {
    return (
      <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#666', fontSize: '14px' }}>Checking access…</span>
      </div>
    );
  }

  if (!isAdminUser) {
    // Redirect non-admins back to root
    window.location.replace('/');
    return null;
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <div style={s.maxW}>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '4px' }}>
          <div style={s.heading}>Admin — Template Management</div>
          <a
            href="/"
            style={{
              fontSize:     '12px',
              color:        '#666',
              textDecoration: 'none',
              marginLeft:   'auto',
              padding:      '5px 12px',
              border:       '1px solid #2a2a2a',
              borderRadius: '6px',
              background:   '#161616',
              cursor:       'pointer',
              flexShrink:   0,
            }}
            onMouseEnter={e => (e.currentTarget.style.color = '#A89FFF')}
            onMouseLeave={e => (e.currentTarget.style.color = '#666')}
          >
            ← Back to Home
          </a>
        </div>
        <div style={s.subheading}>
          Manage carousel templates, test Gemini selector, validate new templates, create from scratch.
        </div>

        {/* ── ⚠ Pending Approvals ────────────────────────────────────────── */}
        {templates.filter(t => !t.active).length > 0 && (
          <>
            <div style={{
              ...s.sectionTitle,
              marginTop:  '0',
              color:      '#FFB84D',
              display:    'flex',
              alignItems: 'center',
              gap:        '8px',
            }}>
              ⚠ Pending Founder Approval
              <span style={{
                fontSize:     '11px',
                background:   '#3a2000',
                color:        '#FFB84D',
                borderRadius: '12px',
                padding:      '2px 8px',
                fontWeight:   700,
              }}>
                {templates.filter(t => !t.active).length}
              </span>
            </div>

            {templates.filter(t => !t.active).map(t => (
              <div key={t.id} style={{
                background:   '#0d1a00',
                border:       '1px solid #3a4a00',
                borderLeft:   '3px solid #FFB84D',
                borderRadius: '10px',
                padding:      '16px 20px',
                marginBottom: '12px',
              }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: '#f0f0f0', fontSize: '15px' }}>{t.name}</span>
                      <span style={{ fontSize: '11px', color: '#555' }}>{t.id}</span>
                      <span style={sBadge(false)}>Inactive</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', lineHeight: '1.5', maxWidth: '600px' }}>
                      {t.description}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    {/* Approve Theme buttons (unapproved themes only) */}
                    {t.themes.filter(th => !th.approved).map(th => (
                      <button
                        key={th.key}
                        style={{
                          padding:      '5px 12px',
                          borderRadius: '6px',
                          border:       '1px solid #555',
                          background:   '#1a1a1a',
                          color:        '#ccc',
                          cursor:       approvingTheme === `${t.id}:${th.key}` ? 'default' : 'pointer',
                          fontSize:     '11px',
                          opacity:      approvingTheme === `${t.id}:${th.key}` ? 0.5 : 1,
                        }}
                        disabled={approvingTheme === `${t.id}:${th.key}`}
                        onClick={() => handleApproveTheme(t.id, th.key)}
                        title={`Approve theme: ${th.label}`}
                      >
                        {approvingTheme === `${t.id}:${th.key}` ? '…' : `✓ Approve ${th.label}`}
                      </button>
                    ))}

                    {/* Activate template */}
                    <button
                      style={{
                        padding:      '5px 14px',
                        borderRadius: '6px',
                        border:       'none',
                        background:   togglingId === t.id ? '#2a4a2a' : '#5fca5f',
                        color:        '#0d0d0d',
                        cursor:       togglingId === t.id ? 'default' : 'pointer',
                        fontSize:     '12px',
                        fontWeight:   700,
                        opacity:      togglingId === t.id ? 0.6 : 1,
                      }}
                      disabled={togglingId === t.id}
                      onClick={() => handleToggle(t.id)}
                    >
                      {togglingId === t.id ? 'Activating…' : '✓ Activate'}
                    </button>

                    {/* Delete template */}
                    <button
                      style={{
                        padding:      '5px 12px',
                        borderRadius: '6px',
                        border:       '1px solid #5a1a1a',
                        background:   '#1a0808',
                        color:        '#ca5f5f',
                        cursor:       'pointer',
                        fontSize:     '12px',
                        fontWeight:   600,
                      }}
                      onClick={() => setConfirmDeleteId(t.id)}
                      title="Permanently delete this template and all its files"
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>

                {/* Preview panel (full render) */}
                <div style={{ marginTop: '12px' }}>
                  <ErrorBoundary
                    fallback={
                      <div style={{ padding: '12px 0', fontSize: '12px', color: '#ca5f5f' }}>
                        Preview failed. Check browser console.
                      </div>
                    }
                  >
                    <TemplatePreviewPanel
                      templateId={t.id}
                      themes={t.themes}
                      active={t.active}
                      useAdminPreview={true}
                      onActivate={() => handleToggle(t.id)}
                      onApproveTheme={(themeKey) => handleApproveTheme(t.id, themeKey)}
                    />
                  </ErrorBoundary>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ── A) Template Analytics Table ────────────────────────────────── */}
        <div style={s.sectionTitle}>A) Template Analytics</div>
        <div style={s.card}>
          {loadingList ? (
            <div style={{ color: '#666', fontSize: '13px' }}>Loading templates…</div>
          ) : templates.length === 0 ? (
            <div style={{ color: '#666', fontSize: '13px' }}>No templates found.</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Template</th>
                  <th style={s.th}>Active</th>
                  <th style={s.th}>Impr.</th>
                  <th style={s.th}>Dwnl.</th>
                  <th style={s.th}>Score</th>
                  <th style={s.th}>Themes</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <React.Fragment key={t.id}>
                    <tr
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                    >
                      <td style={s.td}>
                        <div style={{ fontWeight: 600, color: '#e8e8e8' }}>{t.name}</div>
                        <div style={{ color: '#555', fontSize: '11px' }}>{t.id}</div>
                        {t.autoDisableCandidate && (
                          <div style={{ color: '#ca5f5f', fontSize: '11px', marginTop: '2px' }}>
                            ⚠ auto-disable candidate
                          </div>
                        )}
                      </td>
                      <td style={s.td}>
                        <span style={sBadge(t.active)}>
                          {t.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={s.td}>{t.impressionCount.toLocaleString()}</td>
                      <td style={s.td}>{t.downloadCount.toLocaleString()}</td>
                      <td style={s.td}>
                        {t.score !== null ? (
                          <span style={{ color: t.score < 5 ? '#ca5f5f' : '#5fca5f' }}>
                            {t.score.toFixed(1)}
                          </span>
                        ) : (
                          <span style={{ color: '#444' }}>—</span>
                        )}
                      </td>
                      <td style={s.td}>{t.themes.length}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <button
                            style={{
                              ...s.btnSmall,
                              ...(togglingId === t.id ? { opacity: 0.5 } : {}),
                            }}
                            disabled={togglingId === t.id}
                            onClick={e => { e.stopPropagation(); handleToggle(t.id); }}
                          >
                            {t.active ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            style={{
                              ...s.btnSmall,
                              color:       '#ca5f5f',
                              borderColor: '#5a1a1a',
                              opacity:     t.active ? 0.35 : 1,
                              cursor:      t.active ? 'not-allowed' : 'pointer',
                            }}
                            disabled={t.active || deletingId === t.id}
                            title={t.active ? 'Disable the template first before deleting' : 'Delete permanently'}
                            onClick={e => { e.stopPropagation(); if (!t.active) setConfirmDeleteId(t.id); }}
                          >
                            🗑
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded rows — only for the selected template */}
                    {expandedId === t.id && (
                      <>
                        {/* Row 1: compact themes + metadata */}
                        <tr>
                          <td colSpan={7} style={s.expandRow}>
                            <div style={{ padding: '10px 16px' }}>
                              <div style={{ marginBottom: '6px', fontWeight: 600, color: '#888', fontSize: '11px' }}>
                                THEMES
                              </div>
                              {t.themes.length === 0 ? (
                                <div style={{ color: '#555', fontSize: '12px' }}>No themes configured.</div>
                              ) : (
                                <table style={{ ...s.table, fontSize: '12px' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ ...s.th, fontSize: '11px', padding: '5px 10px' }}>Key</th>
                                      <th style={{ ...s.th, fontSize: '11px', padding: '5px 10px' }}>Label</th>
                                      <th style={{ ...s.th, fontSize: '11px', padding: '5px 10px' }}>Approved</th>
                                      <th style={{ ...s.th, fontSize: '11px', padding: '5px 10px' }}>Impr.</th>
                                      <th style={{ ...s.th, fontSize: '11px', padding: '5px 10px' }}>Dwnl.</th>
                                      <th style={{ ...s.th, fontSize: '11px', padding: '5px 10px' }}>Score</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {t.themes.map(th => (
                                      <tr key={th.key}>
                                        <td style={{ ...s.td, padding: '5px 10px' }}>
                                          <span style={{
                                            display: 'inline-block', width: '10px', height: '10px',
                                            borderRadius: '50%', background: th.accentColor || '#888',
                                            marginRight: '6px', verticalAlign: 'middle',
                                          }} />
                                          {th.key}
                                        </td>
                                        <td style={{ ...s.td, padding: '5px 10px' }}>{th.label}</td>
                                        <td style={{ ...s.td, padding: '5px 10px' }}>
                                          <span style={sBadge(th.approved)}>{th.approved ? 'Yes' : 'No'}</span>
                                          {!th.approved && (
                                            <button
                                              style={{
                                                ...s.btnSmall,
                                                marginLeft: '6px',
                                                background: '#1a2a1a',
                                                color: '#5fca5f',
                                                borderColor: '#2a5a2a',
                                                opacity: approvingTheme === `${t.id}:${th.key}` ? 0.5 : 1,
                                              }}
                                              disabled={approvingTheme === `${t.id}:${th.key}`}
                                              onClick={e => { e.stopPropagation(); handleApproveTheme(t.id, th.key); }}
                                            >
                                              {approvingTheme === `${t.id}:${th.key}` ? '…' : '✓ Approve'}
                                            </button>
                                          )}
                                        </td>
                                        <td style={{ ...s.td, padding: '5px 10px' }}>{th.impressionCount}</td>
                                        <td style={{ ...s.td, padding: '5px 10px' }}>{th.downloadCount}</td>
                                        <td style={{ ...s.td, padding: '5px 10px' }}>
                                          {th.score !== null ? th.score.toFixed(1) : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                              {t.bestFor.length > 0 && (
                                <div style={{ marginTop: '8px', color: '#666', fontSize: '11px' }}>
                                  <strong style={{ color: '#888' }}>Best for:</strong>{' '}{t.bestFor.join(', ')}
                                </div>
                              )}
                              {t.notFor.length > 0 && (
                                <div style={{ color: '#666', fontSize: '11px' }}>
                                  <strong style={{ color: '#888' }}>Not for:</strong>{' '}{t.notFor.join(', ')}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Row 2: full-width preview — own instance per template,
                             so hasFetched resets correctly when switching rows */}
                        <tr>
                          <td colSpan={7} style={{ ...s.expandRow, borderTop: '1px solid #111' }}>
                            <div style={{ padding: '12px 16px 20px' }}>
                              <ErrorBoundary
                                fallback={
                                  <div style={{ padding: '16px 0', fontSize: '12px', color: '#ca5f5f' }}>
                                    Preview failed to render. Check the browser console for details.
                                  </div>
                                }
                              >
                                <TemplatePreviewPanel
                                  templateId={t.id}
                                  themes={t.themes}
                                  active={t.active}
                                  useAdminPreview={!t.active}
                                  onActivate={!t.active ? () => handleToggle(t.id) : undefined}
                                  onApproveTheme={(themeKey) => handleApproveTheme(t.id, themeKey)}
                                />
                              </ErrorBoundary>
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── B) Selector Tester ─────────────────────────────────────────── */}
        <div style={s.sectionTitle}>B) Selector Tester</div>
        <div style={s.card}>
          <div style={{ marginBottom: '10px', fontSize: '13px', color: '#888' }}>
            Test the classification agent. Shows the full decision trace: pre-filter → score ranking → agent candidates → final pick.
          </div>
          <div style={{ display: 'grid', gap: '10px', gridTemplateColumns: '1fr 1fr', marginBottom: '10px' }}>
            <div>
              <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Distilled Insight *</div>
              <textarea
                style={{ ...s.input, minHeight: '80px' }}
                placeholder="The core idea of the post…"
                value={selectorThought}
                onChange={e => setSelectorThought(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Topic</div>
                <input
                  style={s.input}
                  placeholder="e.g. LinkedIn content strategy"
                  value={selectorTopic}
                  onChange={e => setSelectorTopic(e.target.value)}
                />
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Angle</div>
                <select
                  style={{ ...s.select, width: '100%' }}
                  value={selectorAngle}
                  onChange={e => setSelectorAngle(e.target.value)}
                >
                  <option value="thoughtful">thoughtful</option>
                  <option value="contrarian">contrarian</option>
                  <option value="bold">bold</option>
                  <option value="personal">personal</option>
                </select>
              </div>
            </div>
          </div>
          <button
            style={s.btnPrimary}
            onClick={handleTestSelector}
            disabled={selectorLoading || !selectorThought.trim()}
          >
            {selectorLoading ? 'Testing…' : 'Test selector'}
          </button>

          {selectorError && (
            <div style={{ color: '#ca5f5f', fontSize: '13px', marginTop: '10px' }}>
              {selectorError}
            </div>
          )}

          {selectorResult && (() => {
            const tr = selectorResult.trace;
            const phaseLabel = (status: string) =>
              status === 'active'               ? { text: 'ACTIVE',             color: '#4caf50' } :
              status === 'insufficient-data'    ? { text: 'PENDING (need data)', color: '#888' } :
              status === 'insufficient-templates'? { text: 'PENDING (need 5+ templates)', color: '#888' } :
              status === 'feature-gated'        ? { text: 'GATED (100 MAU threshold)', color: '#888' } :
                                                  { text: status.toUpperCase(),  color: '#888' };

            const badge = (t: any, isSelected: boolean, isFiltered: boolean) => (
              <div key={t.id} style={{
                padding: '8px 10px', borderRadius: '6px', marginBottom: '6px',
                background: isSelected ? '#1e2f1e' : isFiltered ? '#2a1a1a' : '#1e1e2e',
                border: `1px solid ${isSelected ? '#4caf50' : isFiltered ? '#c0392b55' : '#333'}`,
                fontSize: '12px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, color: isSelected ? '#4caf50' : isFiltered ? '#c0392b' : '#ccc' }}>
                    {isSelected ? '✓ ' : isFiltered ? '✗ ' : ''}{t.name}
                    <span style={{ color: '#555', fontWeight: 400, marginLeft: '6px' }}>({t.id})</span>
                  </span>
                  <span style={{ color: t.score != null ? '#A89FFF' : '#444', fontSize: '11px' }}>
                    score: {t.score != null ? t.score.toFixed(1) : '—'}
                  </span>
                </div>
                <div style={{ color: '#666', marginTop: '3px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {t.structure && <span>struct: <b style={{ color: '#999' }}>{t.structure}</b></span>}
                  {t.energyLevel != null && <span>energy: <b style={{ color: '#999' }}>{t.energyLevel}/5</b></span>}
                  {t.contentType?.length > 0 && <span>type: <b style={{ color: '#999' }}>{t.contentType.join(', ')}</b></span>}
                  {t.domainTags?.length > 0 && <span>tags: <b style={{ color: '#999' }}>{t.domainTags.join(', ')}</b></span>}
                  {t.filterReason && <span style={{ color: '#c0392b' }}>{t.filterReason}</span>}
                </div>
              </div>
            );

            return (
              <div style={{ marginTop: '14px' }}>
                {/* ── Final result ── */}
                <div style={{ ...sResultCard(true), marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <span style={{ color: '#888', fontSize: '11px' }}>SELECTED TEMPLATE</span>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#A89FFF', marginTop: '2px' }}>
                        {selectorResult.selectedTemplateName ?? selectorResult.selectedTemplateId}
                      </div>
                      <div style={{ fontSize: '11px', color: '#555' }}>id: {selectorResult.selectedTemplateId}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: '#888', fontSize: '11px' }}>COLOR THEME</span>
                      <div style={{ fontSize: '14px', color: '#ccc' }}>{selectorResult.colorTheme}</div>
                    </div>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <span style={{ color: '#888', fontSize: '11px' }}>AGENT REASON</span>
                    <div style={{ fontSize: '13px', color: '#bbb', lineHeight: '1.5', marginTop: '2px' }}>
                      {selectorResult.reason}
                    </div>
                  </div>
                </div>

                {/* ── Decision trace ── */}
                {tr && (
                  <div>
                    <div
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', userSelect: 'none' }}
                      onClick={() => setTraceOpen(o => !o)}
                    >
                      <span style={{ fontSize: '14px', color: '#A89FFF', fontWeight: 600 }}>
                        {traceOpen ? '▼' : '▶'} Decision Trace
                      </span>
                      <span style={{ fontSize: '11px', color: '#555' }}>
                        {tr.allTemplates?.length ?? 0} templates → {tr.filteredOut?.length ?? 0} filtered → {tr.agentCandidates?.length ?? 0} to agent
                      </span>
                    </div>

                    {traceOpen && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

                        {/* Step 1: Input analysis */}
                        <div>
                          <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px', letterSpacing: '0.05em' }}>
                            STEP 1 — INPUT ANALYSIS
                          </div>
                          <div style={{ background: '#111', borderRadius: '6px', padding: '10px', fontSize: '12px', color: '#999', border: '1px solid #222' }}>
                            <div>Insight length: <b style={{ color: '#ccc' }}>{tr.insightLength} chars</b>
                              {tr.insightLength > 200 && <span style={{ color: '#e67e22', marginLeft: '8px' }}>→ triggers structure pre-filter (poster / titles-only excluded)</span>}
                            </div>
                            <div style={{ marginTop: '4px' }}>Topic: <b style={{ color: '#ccc' }}>{selectorTopic.trim() || <span style={{ fontStyle: 'italic', fontWeight: 'normal' }}>auto (first 60 chars)</span>}</b></div>
                            <div style={{ marginTop: '4px' }}>Angle: <b style={{ color: '#ccc' }}>{selectorAngle}</b></div>
                            <div style={{ marginTop: '4px' }}>Total templates available: <b style={{ color: '#ccc' }}>{tr.allTemplates?.length ?? 0}</b></div>
                          </div>
                        </div>

                        {/* Step 2: All templates */}
                        <div>
                          <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px', letterSpacing: '0.05em' }}>
                            STEP 2 — PHASE 1: ALL LOADED TEMPLATES (before filter)
                          </div>
                          {tr.allTemplates?.map((t: any) => {
                            const isFiltered = tr.filteredOut?.some((f: any) => f.id === t.id);
                            return badge({ ...t, filterReason: isFiltered ? tr.filteredOut.find((f: any) => f.id === t.id)?.filterReason : undefined }, false, isFiltered);
                          })}
                        </div>

                        {/* Step 3: Pre-filter */}
                        {tr.filteredOut?.length > 0 && (
                          <div>
                            <div style={{ fontSize: '11px', color: '#c0392b', marginBottom: '6px', letterSpacing: '0.05em' }}>
                              STEP 3 — DETERMINISTIC PRE-FILTER ({tr.filteredOut.length} removed)
                            </div>
                            {tr.filteredOut.map((t: any) => badge(t, false, true))}
                          </div>
                        )}

                        {/* Step 4: Score-ranked pool */}
                        <div>
                          <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px', letterSpacing: '0.05em' }}>
                            STEP 4 — PHASE 1: SCORE-RANKED POOL ({tr.rankedPool?.length ?? 0} survivors, sorted by score ↓)
                          </div>
                          {tr.rankedPool?.map((t: any, i: number) => (
                            <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                              <span style={{ color: '#444', fontSize: '11px', minWidth: '18px', marginTop: '10px' }}>#{i + 1}</span>
                              <div style={{ flex: 1 }}>{badge(t, selectorResult.selectedTemplateId === t.id, false)}</div>
                            </div>
                          ))}
                        </div>

                        {/* Step 5: Agent candidates */}
                        <div>
                          <div style={{ fontSize: '11px', color: '#e67e22', marginBottom: '6px', letterSpacing: '0.05em' }}>
                            STEP 5 — AGENT CANDIDATES (top {tr.agentCandidates?.length ?? 0} passed to Gemini)
                          </div>
                          {tr.agentCandidates?.map((t: any) => badge(t, selectorResult.selectedTemplateId === t.id, false))}
                        </div>

                        {/* Phase 2 — trending */}
                        <div>
                          <div style={{ fontSize: '11px', marginBottom: '6px', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: '#888' }}>PHASE 2 — TRENDING BOOST</span>
                            {tr.phase2 && <span style={{ color: phaseLabel(tr.phase2.status).color, fontSize: '10px', background: '#1a1a1a', padding: '1px 6px', borderRadius: '3px' }}>{phaseLabel(tr.phase2.status).text}</span>}
                          </div>
                          {tr.phase2?.status === 'active' && tr.phase2.boosts ? (
                            tr.phase2.boosts.map((b: any) => (
                              <div key={b.id} style={{ fontSize: '12px', color: '#ccc', padding: '6px 10px', background: '#111', borderRadius: '6px', marginBottom: '4px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                                <span><b style={{ color: '#ddd' }}>{b.name}</b></span>
                                <span style={{ color: '#aaa', fontFamily: 'monospace' }}>
                                  eff={b.effectiveScore?.toFixed(1) ?? '—'}
                                  {b.noveltyBoost > 0 && <span style={{ color: '#4caf50', marginLeft: '6px' }}>+{b.noveltyBoost} novelty</span>}
                                  {b.trendingBoost > 0 && <span style={{ color: '#2196f3', marginLeft: '6px' }}>+{b.trendingBoost.toFixed(1)} trend</span>}
                                  {b.velocityRatio != null && <span style={{ color: '#888', marginLeft: '6px' }}>×{b.velocityRatio.toFixed(2)} vel</span>}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div style={{ fontSize: '12px', color: '#444', padding: '8px 10px', background: '#111', borderRadius: '6px' }}>
                              Data collection live (topic/angle stored in carousel_usage). Trending velocity activates once domain-tagged carousels accumulate.
                            </div>
                          )}
                        </div>

                        {/* Phase 3 — RAG */}
                        <div>
                          <div style={{ fontSize: '11px', marginBottom: '6px', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: '#888' }}>PHASE 3 — RAG EMBEDDING SIMILARITY</span>
                            {tr.phase3 && <span style={{ color: phaseLabel(tr.phase3.status).color, fontSize: '10px', background: '#1a1a1a', padding: '1px 6px', borderRadius: '3px' }}>{phaseLabel(tr.phase3.status).text}</span>}
                          </div>
                          {tr.phase3?.status === 'active' && tr.phase3.similarityScores ? (
                            <>
                              <div style={{ fontSize: '11px', color: '#555', marginBottom: '6px' }}>
                                Cosine similarity between your query and each template embedding (text-embedding-005, 768 dims). Higher = semantically closer.
                              </div>
                              {tr.phase3.similarityScores.map((s: any, idx: number) => {
                                const pct = Math.round(s.cosine * 100);
                                return (
                                  <div key={s.id} style={{ marginBottom: '6px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#ccc', marginBottom: '3px' }}>
                                      <span><span style={{ color: '#555', marginRight: '6px' }}>#{idx + 1}</span><b style={{ color: '#ddd' }}>{s.name}</b></span>
                                      <span style={{ fontFamily: 'monospace', color: '#aaa' }}>{s.cosine.toFixed(4)}</span>
                                    </div>
                                    <div style={{ background: '#1a1a1a', borderRadius: '3px', height: '4px', overflow: 'hidden' }}>
                                      <div style={{ background: '#2196f3', width: `${Math.max(pct, 2)}%`, height: '100%', borderRadius: '3px' }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          ) : tr.phase3?.status === 'disabled' ? (
                            <div style={{ fontSize: '12px', color: '#c0392b', padding: '8px 10px', background: '#1a0a0a', borderRadius: '6px' }}>
                              Embedding API call failed — check Vertex AI credentials.
                            </div>
                          ) : (
                            <div style={{ fontSize: '12px', color: '#444', padding: '8px 10px', background: '#111', borderRadius: '6px' }}>
                              Run <code style={{ color: '#888' }}>npx ts-node src/scripts/refresh-template-embeddings.ts</code> to embed all active templates. Activates once ≥5 templates are embedded.
                            </div>
                          )}
                        </div>

                        {/* Phase 4 — user preference */}
                        <div>
                          <div style={{ fontSize: '11px', marginBottom: '6px', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: '#888' }}>PHASE 4 — USER PREFERENCE</span>
                            {tr.phase4 && <span style={{ color: phaseLabel(tr.phase4.status).color, fontSize: '10px', background: '#1a1a1a', padding: '1px 6px', borderRadius: '3px' }}>{phaseLabel(tr.phase4.status).text}</span>}
                          </div>
                          {tr.phase4?.status === 'active' && tr.phase4.preferenceBoosts ? (
                            tr.phase4.preferenceBoosts.map((b: any) => (
                              <div key={b.id} style={{ fontSize: '12px', color: '#ccc', padding: '6px 10px', background: '#111', borderRadius: '6px', marginBottom: '4px' }}>
                                <b>{b.name}</b>: boost +{b.boost} ({b.priorDownloads} prior downloads in same domain)
                              </div>
                            ))
                          ) : (
                            <div style={{ fontSize: '12px', color: '#444', padding: '8px 10px', background: '#111', borderRadius: '6px' }}>
                              Data collection active (user_template_preference table). Boost enabled via ENABLE_USER_PREFERENCE_BOOST=true at ≥100 MAU.
                            </div>
                          )}
                        </div>

                        {/* Broken templates */}
                        {selectorResult.brokenTemplates?.length > 0 && (
                          <div>
                            <div style={{ fontSize: '11px', color: '#c0392b', marginBottom: '6px', letterSpacing: '0.05em' }}>
                              BROKEN MANIFESTS (excluded entirely)
                            </div>
                            {selectorResult.brokenTemplates.map((b: any) => (
                              <div key={b.id} style={{ fontSize: '12px', color: '#c0392b', padding: '6px 10px', background: '#1a0a0a', borderRadius: '6px', marginBottom: '4px' }}>
                                <b>{b.id}</b>: {b.reason}
                              </div>
                            ))}
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* ── C) Template Creation Guide ─────────────────────────────────── */}
        <div style={s.sectionTitle}>C) Template Creation Guide</div>
        <div style={s.card}>
          <div style={{ marginBottom: '16px' }}>
            <button style={sTab(guideTab === 'html')} onClick={() => setGuideTab('html')}>
              HTML Template
            </button>
            <button style={sTab(guideTab === 'pptx')} onClick={() => setGuideTab('pptx')}>
              PPTX Template
            </button>
          </div>

          {guideTab === 'html' && (
            <div>
              <div style={{ fontSize: '13px', color: '#888', marginBottom: '10px' }}>
                Use this prompt with an AI (Claude or Gemini) to generate a new HTML template
                from scratch. Copy it, describe your desired design, and paste the resulting HTML
                into the Validator below before wiring it up.
              </div>
              <pre style={s.pre}>{HTML_TEMPLATE_PROMPT}</pre>
              <button
                style={{ ...s.btnSecondary, marginTop: '10px' }}
                onClick={() => handleCopyPrompt(HTML_TEMPLATE_PROMPT)}
              >
                {copiedPrompt ? 'Copied!' : 'Copy prompt'}
              </button>
            </div>
          )}

          {guideTab === 'pptx' && (
            <div>
              <div style={{ fontSize: '13px', color: '#888', marginBottom: '10px', lineHeight: '1.7' }}>
                <strong style={{ color: '#ccc' }}>Workflow:</strong>
                <ol style={{ margin: '8px 0', paddingLeft: '18px' }}>
                  <li>Design your slides in PowerPoint at 1080×1080 or 1200×1500.</li>
                  <li>Screenshot each slide (or export as PNG).</li>
                  <li>Use the prompt below to convert the layout to HTML/CSS.</li>
                  <li>Tell the AI the slide dimensions, font names, and share the screenshots.</li>
                  <li>Validate the resulting HTML in section D below.</li>
                </ol>
                <strong style={{ color: '#ccc' }}>Reference example:</strong>{' '}
                <code style={{ color: '#A89FFF', fontSize: '12px' }}>
                  Node/src/templates/carousel/pink.html
                </code>{' '}
                — PPTX-converted Social Media Playbook template.
              </div>
              <pre style={s.pre}>{PPTX_TO_HTML_PROMPT}</pre>
              <button
                style={{ ...s.btnSecondary, marginTop: '10px' }}
                onClick={() => handleCopyPrompt(PPTX_TO_HTML_PROMPT)}
              >
                {copiedPrompt ? 'Copied!' : 'Copy prompt'}
              </button>
            </div>
          )}
        </div>

        {/* ── D) Validation Checker ──────────────────────────────────────── */}
        <div style={s.sectionTitle}>D) Template Validator</div>
        <div style={s.card}>
          <div style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
            Paste a new HTML template here to validate it before adding it to the system.
          </div>

          {/* Template type toggle */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
            <button
              style={sTab(validateType === 'html')}
              onClick={() => { setValidateType('html'); setValidatePptxFile(null); }}
            >
              HTML Template
            </button>
            <button
              style={sTab(validateType === 'pptx')}
              onClick={() => setValidateType('pptx')}
            >
              PPTX Template
            </button>
          </div>

          <div style={{ marginBottom: '10px' }}>
            <input
              style={{ ...s.input, resize: 'none' as const }}
              placeholder="templateId (e.g. yellow, bw, darkblue)"
              value={validateId}
              onChange={e => setValidateId(e.target.value)}
            />
          </div>

          {/* PPTX file upload — only shown for PPTX type */}
          {validateType === 'pptx' && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                Source PPTX file (optional — will be saved as{' '}
                <code style={{ color: '#A89FFF' }}>
                  templates/carousel/pptx/{validateId || '<templateId>'}.pptx
                </code>
                )
              </div>
              <input
                type="file"
                accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                style={{ ...s.input, padding: '8px', cursor: 'pointer' }}
                onChange={e => setValidatePptxFile(e.target.files?.[0] ?? null)}
              />
              {validatePptxFile && (
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                  Selected: {validatePptxFile.name} ({(validatePptxFile.size / 1024).toFixed(0)} KB)
                </div>
              )}
            </div>
          )}

          <textarea
            style={{ ...s.input, minHeight: '180px', marginBottom: '10px', fontFamily: 'monospace' }}
            placeholder="Paste full HTML template here…"
            value={validateHtml}
            onChange={e => setValidateHtml(e.target.value)}
          />
          <button
            style={s.btnPrimary}
            onClick={handleValidate}
            disabled={validateLoading || !validateHtml.trim()}
          >
            {validateLoading ? 'Validating…' : 'Validate'}
          </button>

          {validateResult && (
            <div style={sResultCard(validateResult.valid)}>
              <div style={{
                fontSize:   '15px',
                fontWeight: 700,
                color:      validateResult.valid ? '#5fca5f' : '#ca5f5f',
                marginBottom: '8px',
              }}>
                {validateResult.valid ? '✓ Valid template' : '✗ Validation failed'}
              </div>
              {validateResult.pptxSaved && (
                <div style={{
                  display:      'inline-flex',
                  alignItems:   'center',
                  gap:          '6px',
                  background:   '#0f2a0f',
                  border:       '1px solid #2a5a2a',
                  borderRadius: '6px',
                  padding:      '4px 10px',
                  fontSize:     '12px',
                  color:        '#5fca5f',
                  marginBottom: '8px',
                }}>
                  PPTX saved ✓
                </div>
              )}
              {validateResult.errors.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#ca8a8a', lineHeight: '1.8' }}>
                  {validateResult.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              )}
              {validateResult.valid && (
                <div style={{ fontSize: '13px', color: '#5fca5f' }}>
                  All checks passed. Template is ready to be added to the system.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom padding */}
        <div style={{ height: '60px' }} />

      </div>

      {/* ── Delete confirmation overlay ────────────────────────────────────── */}
      {confirmDeleteId && (() => {
        const tpl = templates.find(t => t.id === confirmDeleteId);
        return (
          <div style={{
            position:       'fixed',
            inset:          0,
            zIndex:         1000,
            background:     'rgba(0,0,0,0.75)',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            padding:        '24px',
          }}>
            <div style={{
              background:   '#161616',
              border:       '1px solid #5a1a1a',
              borderRadius: '12px',
              padding:      '28px 32px',
              maxWidth:     '480px',
              width:        '100%',
            }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#ca5f5f', marginBottom: '8px' }}>
                ⚠ Delete Template
              </div>
              <div style={{ fontSize: '14px', color: '#ccc', marginBottom: '8px', lineHeight: '1.6' }}>
                You are about to permanently delete{' '}
                <strong style={{ color: '#f0f0f0' }}>"{tpl?.name}" ({confirmDeleteId})</strong>.
              </div>
              <div style={{
                background:   '#1a0d0d',
                border:       '1px solid #3a1a1a',
                borderRadius: '8px',
                padding:      '12px 14px',
                fontSize:     '12px',
                color:        '#ca8a8a',
                lineHeight:   '1.7',
                marginBottom: '20px',
              }}>
                This will permanently remove:
                <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                  <li>All theme rows from the database</li>
                  <li>The template row from the database</li>
                  <li>The manifest JSON file from disk</li>
                  <li>The PPTX or HTML template file from disk</li>
                </ul>
                <strong style={{ color: '#e06060' }}>This cannot be undone.</strong>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  style={s.btnSecondary}
                  onClick={() => setConfirmDeleteId(null)}
                >
                  Cancel
                </button>
                <button
                  style={{
                    padding:      '8px 20px',
                    borderRadius: '8px',
                    border:       'none',
                    background:   '#ca5f5f',
                    color:        '#fff',
                    cursor:       deletingId === confirmDeleteId ? 'default' : 'pointer',
                    fontSize:     '13px',
                    fontWeight:   700,
                    opacity:      deletingId === confirmDeleteId ? 0.6 : 1,
                  }}
                  disabled={deletingId === confirmDeleteId}
                  onClick={() => handleDelete(confirmDeleteId)}
                >
                  {deletingId === confirmDeleteId ? 'Deleting…' : 'Yes, delete permanently'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AdminTemplates;
